import { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Floating chat bubble component.
 * Users can ask questions which run in the background and provide updates.
 */
export function ChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        popupRef.current &&
        buttonRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Popup */}
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute bottom-16 right-0 w-96 rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 bg-coilive-blue px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-white/20 p-1.5">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold text-white">Ask COI LIVE</span>
              <span className="rounded-full bg-coilive-gold px-2 py-0.5 text-xs font-medium text-white">
                Coming Soon
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Chat Area - Placeholder */}
          <div className="h-64 overflow-y-auto p-4">
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-3 rounded-full bg-coilive-blue/10 p-4">
                <Sparkles className="h-8 w-8 text-coilive-blue" />
              </div>
              <h3 className="mb-2 font-semibold text-gray-800">AI-Powered Assistance</h3>
              <p className="mb-4 text-sm text-gray-500">
                Ask questions about your compliance data and get instant answers.
              </p>
              <div className="space-y-2 text-left text-sm text-gray-500">
                <p className="rounded-lg bg-gray-50 px-3 py-2">
                  "Which insureds are non-compliant?"
                </p>
                <p className="rounded-lg bg-gray-50 px-3 py-2">
                  "Show COIs expiring this month"
                </p>
                <p className="rounded-lg bg-gray-50 px-3 py-2">
                  "Send reminder to ABC Corp"
                </p>
              </div>
            </div>
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-100 p-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask a question..."
                disabled
                className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-400 placeholder:text-gray-400"
              />
              <button
                disabled
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200 text-gray-400"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-center text-xs text-gray-400">
              Questions will run in the background and notify you when ready.
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-200',
          'bg-coilive-blue hover:bg-coilive-blue-700 hover:shadow-xl',
          isOpen && 'ring-4 ring-coilive-blue/30'
        )}
        aria-label="Open AI Assistant"
      >
        <Bot className="h-6 w-6 text-white" />
      </button>
    </div>
  );
}
